/**
 * TMX Map Renderer
 *
 * Renders TMX maps to canvas with support for:
 * - Chunked infinite maps
 * - Tile flipping (horizontal, vertical, diagonal)
 * - Animated tiles
 * - Multiple layers with offsets
 * - Viewport culling for performance
 */

import type { TMXMap, TMXTileset, TMXLayer, TMXChunk, TMXViewport } from "@/types/tmx.types"
import { getTileData, getAnimatedSrcCoords } from "./tmx-parser"
import { TILE_SIZE } from "@/data/game-settings"

// TMX tiles are 16px, game uses 64px tiles - scale factor is 4x
const TMX_TILE_SIZE = 16
const SCALE_FACTOR = TILE_SIZE / TMX_TILE_SIZE // 4

// Image cache for tileset images
const tilesetImageCache = new Map<string, HTMLImageElement>()

/**
 * Rendering pass classification for elevation-aware rendering
 * - "true_background": Ground level - always behind entities
 * - "elevation": Elevated terrain - Y-sorted with entities
 * - "true_foreground": Canopies/overhead - always above entities
 */
type TMXRenderPass = "true_background" | "elevation" | "true_foreground"

// Layer classification by rendering pass
const LAYER_RENDER_PASS: Record<string, TMXRenderPass> = {
  // True background - ground level, always behind entities
  "water background": "true_background",
  "water lighting": "true_background",
  "water lighting2": "true_background",
  "water lighting3": "true_background",
  "main space": "true_background",
  "ground shadow": "true_background",
  "grass shadow": "true_background",

  // Elevation layers - Y-sorted with entities
  "elevated_space": "elevation",
  "objects_under_columns": "elevation",
  "columns": "elevation",

  // True foreground - always above entities (canopies, overhead)
  "details": "true_foreground",
  "objects1": "true_foreground",
  "objects2": "true_foreground",
  "objects3": "true_foreground",
}

// Layer filter type for split rendering
export type TMXLayerFilter = "true_background" | "elevation" | "true_foreground" | "all"

/**
 * Load a tileset image, using cache
 */
function loadTilesetImage(imagePath: string): HTMLImageElement {
  // Normalize path - TMX paths are relative to the TMX file location
  // TMX files are in /images/biomes/grassland/, so relative paths need that prefix
  const normalizedPath = imagePath.startsWith("/")
    ? imagePath
    : `/images/biomes/grassland/${imagePath}`

  if (tilesetImageCache.has(normalizedPath)) {
    return tilesetImageCache.get(normalizedPath)!
  }

  const img = new Image()
  img.src = normalizedPath
  tilesetImageCache.set(normalizedPath, img)
  return img
}

/**
 * Draw a single tile with flip transformations
 */
function drawTile(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  srcX: number,
  srcY: number,
  destX: number,
  destY: number,
  srcSize: number,
  destSize: number,
  flipH: boolean,
  flipV: boolean,
  flipD: boolean,
): void {
  if (!img.complete || img.naturalWidth === 0) return

  // If no transforms, draw directly (faster)
  if (!flipH && !flipV && !flipD) {
    ctx.drawImage(img, srcX, srcY, srcSize, srcSize, destX, destY, destSize, destSize)
    return
  }

  // Apply transforms
  ctx.save()
  ctx.translate(destX + destSize / 2, destY + destSize / 2)

  // Diagonal flip = transpose (rotate 90 degrees clockwise + flip vertically).
  // When flipD is set, H/V act on the transposed axes - swapping them here is
  // what makes Tiled's "rotate 90 CW" (H+D) and "rotate 90 CCW" (V+D) correct.
  if (flipD) {
    ctx.rotate(Math.PI / 2)
    ctx.scale(1, -1)
    if (flipH) ctx.scale(1, -1)
    if (flipV) ctx.scale(-1, 1)
  } else {
    if (flipH) ctx.scale(-1, 1)
    if (flipV) ctx.scale(1, -1)
  }

  ctx.drawImage(
    img,
    srcX,
    srcY,
    srcSize,
    srcSize,
    -destSize / 2,
    -destSize / 2,
    destSize,
    destSize,
  )
  ctx.restore()
}

/**
 * Check if a chunk intersects the viewport
 */
function chunkIntersectsViewport(
  chunk: TMXChunk,
  viewport: TMXViewport,
  layerOffsetX: number,
  layerOffsetY: number,
): boolean {
  // Chunk bounds in world pixels (scaled)
  const chunkLeft = (chunk.x * TMX_TILE_SIZE + layerOffsetX) * SCALE_FACTOR
  const chunkTop = (chunk.y * TMX_TILE_SIZE + layerOffsetY) * SCALE_FACTOR
  const chunkRight = chunkLeft + chunk.width * TILE_SIZE
  const chunkBottom = chunkTop + chunk.height * TILE_SIZE

  // Viewport bounds
  const viewLeft = viewport.x
  const viewTop = viewport.y
  const viewRight = viewport.x + viewport.width
  const viewBottom = viewport.y + viewport.height

  return chunkRight > viewLeft && chunkLeft < viewRight && chunkBottom > viewTop && chunkTop < viewBottom
}

/**
 * Render a single layer's chunks
 */
function renderLayer(
  ctx: CanvasRenderingContext2D,
  layer: TMXLayer,
  tilesets: TMXTileset[],
  viewport: TMXViewport,
  gameTimeMs: number,
): void {
  if (!layer.visible || layer.opacity === 0) return

  // Apply layer opacity
  const prevAlpha = ctx.globalAlpha
  ctx.globalAlpha *= layer.opacity

  // Layer offset in source pixels (TMX coordinates)
  const layerOffsetX = layer.offsetX
  const layerOffsetY = layer.offsetY

  for (const chunk of layer.chunks) {
    // Viewport culling - skip chunks outside view
    if (!chunkIntersectsViewport(chunk, viewport, layerOffsetX, layerOffsetY)) {
      continue
    }

    // Render each tile in the chunk
    for (let i = 0; i < chunk.data.length; i++) {
      const rawGid = chunk.data[i]
      if (rawGid === 0) continue // Empty tile

      // Get tile data (tileset, source coords, flip flags)
      const tileData = getTileData(rawGid, tilesets)
      if (!tileData) continue

      // Calculate tile position within chunk
      const tileX = i % chunk.width
      const tileY = Math.floor(i / chunk.width)

      // World position in TMX pixels (before scaling)
      const worldTileX = chunk.x + tileX
      const worldTileY = chunk.y + tileY

      // Destination position in game pixels (scaled, with layer offset)
      const destX = (worldTileX * TMX_TILE_SIZE + layerOffsetX) * SCALE_FACTOR
      const destY = (worldTileY * TMX_TILE_SIZE + layerOffsetY) * SCALE_FACTOR

      // Skip tiles outside viewport (fine-grained culling)
      if (
        destX + TILE_SIZE < viewport.x ||
        destX > viewport.x + viewport.width ||
        destY + TILE_SIZE < viewport.y ||
        destY > viewport.y + viewport.height
      ) {
        continue
      }

      // Get tileset image
      const img = loadTilesetImage(tileData.tileset.image)

      // Get animated source coordinates
      const { srcX, srcY } = getAnimatedSrcCoords(
        tileData.tileset,
        tileData.localId,
        gameTimeMs,
      )

      // Draw the tile
      drawTile(
        ctx,
        img,
        srcX,
        srcY,
        destX,
        destY,
        TMX_TILE_SIZE,
        TILE_SIZE,
        tileData.flipH,
        tileData.flipV,
        tileData.flipD,
      )
    }
  }

  ctx.globalAlpha = prevAlpha
}

/**
 * Main function to render a TMX map
 *
 * @param ctx - Canvas 2D rendering context
 * @param tmxMap - Parsed TMX map data
 * @param viewport - Current viewport (camera position and size)
 * @param gameTimeMs - Game time in milliseconds (for animations)
 * @param layerFilter - Which layers to render:
 *   - "true_background": Ground level (water, main space, shadows)
 *   - "elevation": Elevated terrain (Y-sorted with entities)
 *   - "true_foreground": Canopies/overhead (always above entities)
 *   - "all": All layers
 */
export function drawTMXMap(
  ctx: CanvasRenderingContext2D,
  tmxMap: TMXMap,
  viewport: TMXViewport,
  gameTimeMs: number = Date.now(),
  layerFilter: TMXLayerFilter = "all",
): void {
  ctx.save()
  ctx.imageSmoothingEnabled = false // Pixel-perfect rendering

  // Render layers bottom to top (array order is render order)
  for (const layer of tmxMap.layers) {
    const layerPass = LAYER_RENDER_PASS[layer.name] || "true_background"

    // Filter layers based on requested filter
    if (layerFilter !== "all" && layerFilter !== layerPass) continue

    renderLayer(ctx, layer, tmxMap.tilesets, viewport, gameTimeMs)
  }

  ctx.restore()
}

/**
 * Render elevation layer tiles within a specific Y range
 * Used for Y-sorted interleaving with entities
 *
 * @param ctx - Canvas context
 * @param tmxMap - The TMX map
 * @param viewport - Current viewport
 * @param yMin - Minimum world Y (inclusive)
 * @param yMax - Maximum world Y (exclusive)
 * @param gameTimeMs - For animations
 */
export function drawTMXElevationRow(
  ctx: CanvasRenderingContext2D,
  tmxMap: TMXMap,
  viewport: TMXViewport,
  yMin: number,
  yMax: number,
  gameTimeMs: number,
): void {
  ctx.save()
  ctx.imageSmoothingEnabled = false

  // Filter to elevation layers only
  for (const layer of tmxMap.layers) {
    const layerPass = LAYER_RENDER_PASS[layer.name]
    if (layerPass !== "elevation") continue

    renderLayerInYRange(ctx, layer, tmxMap.tilesets, viewport, yMin, yMax, gameTimeMs)
  }

  ctx.restore()
}

/**
 * Render a layer's tiles only within a specific Y range
 */
function renderLayerInYRange(
  ctx: CanvasRenderingContext2D,
  layer: TMXLayer,
  tilesets: TMXTileset[],
  viewport: TMXViewport,
  yMin: number,
  yMax: number,
  gameTimeMs: number,
): void {
  if (!layer.visible || layer.opacity === 0) return

  const prevAlpha = ctx.globalAlpha
  ctx.globalAlpha *= layer.opacity

  const layerOffsetX = layer.offsetX
  const layerOffsetY = layer.offsetY

  for (const chunk of layer.chunks) {
    // Quick viewport culling
    if (!chunkIntersectsViewport(chunk, viewport, layerOffsetX, layerOffsetY)) {
      continue
    }

    for (let i = 0; i < chunk.data.length; i++) {
      const rawGid = chunk.data[i]
      if (rawGid === 0) continue

      const tileData = getTileData(rawGid, tilesets)
      if (!tileData) continue

      const tileX = i % chunk.width
      const tileY = Math.floor(i / chunk.width)

      const worldTileX = chunk.x + tileX
      const worldTileY = chunk.y + tileY

      const destX = (worldTileX * TMX_TILE_SIZE + layerOffsetX) * SCALE_FACTOR
      const destY = (worldTileY * TMX_TILE_SIZE + layerOffsetY) * SCALE_FACTOR

      // Y-range filter: only render tiles whose bottom edge is within [yMin, yMax)
      const tileBottomY = destY + TILE_SIZE
      if (tileBottomY < yMin || tileBottomY >= yMax) continue

      // Viewport culling
      if (
        destX + TILE_SIZE < viewport.x ||
        destX > viewport.x + viewport.width ||
        destY + TILE_SIZE < viewport.y ||
        destY > viewport.y + viewport.height
      ) {
        continue
      }

      const img = loadTilesetImage(tileData.tileset.image)
      const { srcX, srcY } = getAnimatedSrcCoords(tileData.tileset, tileData.localId, gameTimeMs)

      drawTile(ctx, img, srcX, srcY, destX, destY, TMX_TILE_SIZE, TILE_SIZE, tileData.flipH, tileData.flipV, tileData.flipD)
    }
  }

  ctx.globalAlpha = prevAlpha
}

/**
 * Preload all tileset images for a TMX map
 * Call this before rendering to avoid pop-in
 */
export function preloadTMXImages(tmxMap: TMXMap): Promise<void[]> {
  const promises: Promise<void>[] = []

  for (const tileset of tmxMap.tilesets) {
    const img = loadTilesetImage(tileset.image)
    if (!img.complete) {
      promises.push(
        new Promise((resolve) => {
          img.onload = () => resolve()
          // Resolve on error too (matching the other preloaders) so one bad
          // tileset doesn't reject the whole batch as an unhandled rejection
          img.onerror = () => {
            console.warn(`Failed to load tileset: ${tileset.image}`)
            resolve()
          }
        }),
      )
    }
  }

  return Promise.all(promises)
}

/**
 * Get the world bounds of a TMX map in game pixels
 */
export function getTMXMapBounds(tmxMap: TMXMap): { width: number; height: number } {
  // For infinite maps, calculate from chunk extents
  let minX = 0,
    minY = 0,
    maxX = 0,
    maxY = 0

  for (const layer of tmxMap.layers) {
    for (const chunk of layer.chunks) {
      minX = Math.min(minX, chunk.x)
      minY = Math.min(minY, chunk.y)
      maxX = Math.max(maxX, chunk.x + chunk.width)
      maxY = Math.max(maxY, chunk.y + chunk.height)
    }
  }

  // Convert to game pixels (scaled)
  return {
    width: (maxX - minX) * TILE_SIZE,
    height: (maxY - minY) * TILE_SIZE,
  }
}
